import { styled } from '@mui/material/styles';
import { getIdvThemeTokens } from '../themeTokens';

export const HistoryRoot = styled('section')(({ theme }) => {
  const t = getIdvThemeTokens(theme);
  return {
    marginTop: 26,
    border: `1px solid ${t.border}`,
    borderRadius: 10,
    overflow: 'hidden',
    background: t.surface,
  };
});

export const HistoryHeading = styled('div')(({ theme }) => {
  const t = getIdvThemeTokens(theme);
  return {
    margin: 0,
    padding: '14px 16px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 16,
    borderBottom: `1px solid ${t.border}`,
    [theme.breakpoints.down(760)]: {
      alignItems: 'stretch',
      flexDirection: 'column',
    },
  };
});

export const HistoryEyebrow = styled('p')(({ theme }) => ({
  margin: '0 0 4px',
  color: getIdvThemeTokens(theme).textMuted,
  fontSize: 10,
  fontWeight: 700,
  letterSpacing: '0.08em',
  textTransform: 'uppercase',
}));

export const HistoryTitle = styled('h3')(({ theme }) => ({
  margin: 0,
  color: getIdvThemeTokens(theme).text,
  fontSize: 15,
  fontWeight: 700,
}));

export const NotesButton = styled('a')(({ theme }) => {
  const t = getIdvThemeTokens(theme);
  return {
    flex: '0 0 auto',
    minHeight: 34,
    padding: '7px 12px',
    display: 'inline-flex',
    alignItems: 'center',
    border: `1px solid ${t.borderStrong}`,
    borderRadius: 7,
    color: t.markSecondaryText,
    background: t.surface,
    fontSize: 12,
    fontWeight: 600,
    textDecoration: 'none',
    '&:hover': {
      borderColor: t.chipHoverBorder,
      color: t.violetDark,
    },
  };
});

export const TableWrap = styled('div')({
  overflowX: 'auto',
});

export const HistoryTable = styled('table')({
  width: '100%',
  borderCollapse: 'collapse',
  fontSize: 12,
});

export const HeaderCell = styled('th')(({ theme }) => ({
  padding: '9px 12px',
  color: getIdvThemeTokens(theme).textMuted,
  background: getIdvThemeTokens(theme).surfaceSoft,
  textAlign: 'left',
  fontSize: 10,
  fontWeight: 700,
  letterSpacing: '0.04em',
  textTransform: 'uppercase',
  whiteSpace: 'nowrap',
}));

export const FilterButton = styled('button', {
  shouldForwardProp: (prop) => prop !== 'filtered',
})<{ filtered?: boolean }>(({ theme, filtered }) => {
  const t = getIdvThemeTokens(theme);
  return {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 5,
    margin: '-4px -6px',
    padding: '4px 6px',
    border: `1px solid ${filtered ? t.aiBadgeBorder : 'transparent'}`,
    borderRadius: 6,
    background: filtered ? t.violetSoft : 'none',
    color: filtered ? t.violetDark : 'inherit',
    font: 'inherit',
    letterSpacing: 'inherit',
    textTransform: 'inherit',
    cursor: 'pointer',
    '&:hover': {
      borderColor: filtered ? t.aiBadgeBorder : t.chipHoverBorder,
      color: filtered ? t.violetDark : t.stepLabel,
    },
  };
});

export const Funnel = styled('span', {
  shouldForwardProp: (prop) => prop !== 'filtered',
})<{ filtered?: boolean }>(({ filtered }) => ({
  fontSize: 9,
  opacity: filtered ? 1 : 0.7,
}));

export const FilterBar = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  flexWrap: 'wrap',
  gap: 8,
  padding: '10px 16px',
  borderBottom: `1px solid ${getIdvThemeTokens(theme).border}`,
}));

export const FilterChips = styled('div')({
  display: 'flex',
  flexWrap: 'wrap',
  gap: 6,
});

export const FilterChip = styled('span')(({ theme }) => {
  const t = getIdvThemeTokens(theme);
  return {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 5,
    padding: '2px 4px 2px 8px',
    borderRadius: 999,
    background: t.violetSoft,
    color: t.violetDark,
    fontSize: 11,
    fontWeight: 600,
  };
});

export const ChipClear = styled('button')(({ theme }) => ({
  width: 16,
  height: 16,
  display: 'grid',
  placeItems: 'center',
  border: 'none',
  borderRadius: '50%',
  background: getIdvThemeTokens(theme).surface,
  color: 'inherit',
  cursor: 'pointer',
  fontSize: 12,
  lineHeight: 1,
}));

export const ClearFiltersButton = styled('button')(({ theme }) => ({
  padding: 0,
  border: 'none',
  background: 'none',
  color: getIdvThemeTokens(theme).violetDark,
  fontSize: 11,
  fontWeight: 700,
  cursor: 'pointer',
  textDecoration: 'underline',
}));

export const FilterPopover = styled('div')(({ theme }) => {
  const t = getIdvThemeTokens(theme);
  return {
    position: 'fixed',
    zIndex: 1301,
    width: 'min(280px, calc(100vw - 32px))',
    padding: 14,
    display: 'grid',
    gap: 10,
    border: `1px solid ${t.border}`,
    borderRadius: 12,
    color: t.text,
    background: t.surface,
    colorScheme: t.colorScheme,
    boxShadow: t.overlayShadow,
  };
});

export const FilterPopoverHead = styled('div')({
  display: 'flex',
  alignItems: 'flex-start',
  justifyContent: 'space-between',
  gap: 12,
});

export const FilterEyebrow = styled('p')(({ theme }) => ({
  margin: '0 0 2px',
  color: getIdvThemeTokens(theme).textMuted,
  fontSize: 10,
  fontWeight: 700,
  letterSpacing: '0.08em',
  textTransform: 'uppercase',
}));

export const FilterPopoverTitle = styled('h4')({
  margin: 0,
  fontSize: 14,
});

export const FilterClose = styled('button')(({ theme }) => {
  const t = getIdvThemeTokens(theme);
  return {
    width: 28,
    height: 28,
    border: `1px solid ${t.border}`,
    borderRadius: 7,
    background: t.surface,
    color: t.textMeta,
    cursor: 'pointer',
    fontSize: 16,
    lineHeight: 1,
  };
});

export const FilterSearch = styled('input')(({ theme }) => {
  const t = getIdvThemeTokens(theme);
  return {
    width: '100%',
    padding: '7px 9px',
    border: `1px solid ${t.border}`,
    borderRadius: 8,
    color: t.text,
    background: t.surface,
    colorScheme: t.colorScheme,
    font: 'inherit',
    fontSize: 12,
    boxSizing: 'border-box',
    '&::placeholder': {
      color: t.textMuted,
    },
    '&:focus': {
      outline: 'none',
      borderColor: t.violet,
      boxShadow: `0 0 0 3px ${t.violetSoft}`,
    },
  };
});

export const FilterValues = styled('div')({
  maxHeight: 220,
  overflowY: 'auto',
  display: 'grid',
  gap: 1,
});

export const FilterCheck = styled('label', {
  shouldForwardProp: (prop) => prop !== 'all',
})<{ all?: boolean }>(({ theme, all }) => {
  const t = getIdvThemeTokens(theme);
  return {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    padding: all ? '5px 6px 8px' : '5px 6px',
    borderBottom: all ? `1px solid ${t.border}` : 'none',
    borderRadius: all ? 0 : 6,
    color: t.stepLabel,
    fontSize: 12,
    fontWeight: all ? 700 : 400,
    cursor: 'pointer',
    '& input': {
      accentColor: t.violet,
    },
    '&:hover': {
      background: all ? 'none' : t.surfaceSoft,
    },
  };
});

export const FilterValueLabel = styled('span')({
  flex: 1,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
});

export const FilterCount = styled('em')(({ theme }) => ({
  color: getIdvThemeTokens(theme).textMuted,
  fontStyle: 'normal',
  fontSize: 11,
  fontWeight: 600,
}));

export const FilterPopoverFoot = styled('div')({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: 10,
});

export const FilterLink = styled('button')(({ theme }) => ({
  padding: 0,
  border: 'none',
  background: 'none',
  color: getIdvThemeTokens(theme).violetDark,
  fontSize: 11,
  fontWeight: 700,
  cursor: 'pointer',
  textDecoration: 'underline',
}));

export const FilterDone = styled('button')(({ theme }) => {
  const t = getIdvThemeTokens(theme);
  return {
    minHeight: 32,
    padding: '6px 12px',
    border: 'none',
    borderRadius: 7,
    background: t.violet,
    color: t.markPrimaryText,
    fontSize: 12,
    fontWeight: 700,
    cursor: 'pointer',
  };
});

export const BodyCell = styled('td')(({ theme }) => ({
  padding: '10px 12px',
  borderTop: `1px solid ${getIdvThemeTokens(theme).border}`,
  color: getIdvThemeTokens(theme).textMeta,
  verticalAlign: 'top',
}));

export const EventName = styled('span')(({ theme }) => ({
  color: getIdvThemeTokens(theme).stepLabel,
  fontWeight: 600,
}));

export const AttemptTag = styled('span')(({ theme }) => {
  const t = getIdvThemeTokens(theme);
  return {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 4,
    padding: '2px 7px',
    borderRadius: 5,
    color: t.stepLabel,
    background: t.tagDefaultBg,
    fontSize: 11,
    fontWeight: 600,
    whiteSpace: 'nowrap',
  };
});

export const CaseTag = styled(AttemptTag)(({ theme }) => {
  const t = getIdvThemeTokens(theme);
  return {
    color: t.textMeta,
    background: t.surfaceSoft,
    border: `1px dashed ${t.border}`,
  };
});

export const AiMark = styled('span')(({ theme }) => {
  const t = getIdvThemeTokens(theme);
  return {
    padding: '0 4px',
    borderRadius: 3,
    background: t.violetSoft,
    color: t.aiText,
    border: `1px solid ${t.aiBadgeBorder}`,
    fontSize: 9,
    letterSpacing: '0.04em',
  };
});

export const EmptyNote = styled('p')(({ theme }) => {
  const t = getIdvThemeTokens(theme);
  return {
    margin: '12px 16px',
    padding: '22px 12px',
    border: `1px dashed ${t.border}`,
    borderRadius: 10,
    color: t.textMuted,
    fontSize: 12,
    textAlign: 'center',
  };
});
